<template>
  <div class="metadata-modal">
    <div class="body" ref="body">
      <type-field 
        :root="rootNode" 
        :type="formData.type" 
        :limitCompType="limitCompType"
      ></type-field>
      <div class="input-block grid-col-1-3 grid-row-2-3">
        <label>
          Label
          <span class="required">*</span>
          <tooltip :text="TOOLTIP_TEXT.INPUT_LABEL"></tooltip>
        </label>
        <input
          type="text"
          v-model="formData.label"
          name="label"
          required
          @blur="generateFieldName()"
        />
      </div>
      <div class="input-block grid-row-2-3">
        <label>
          Hint
          <tooltip :text="TOOLTIP_TEXT.HINT"></tooltip>
        </label>
        <input type="text" v-model="formData.hint" name="hint" />
      </div>
      <div class="checkbox-block grid-row-3-4">
        <input
          type="checkbox"
          name="required"
          id="metadata-checkbox-1"
          class="checkbox-input"
          v-model="formData.required"
        />
        <label for="metadata-checkbox-1">
          Required
          <tooltip :text="TOOLTIP_TEXT.REQUIRED"></tooltip>
        </label>
      </div>
      <div class="checkbox-block grid-row-3-4">
        <input
          type="checkbox"
          name="readOnlyIfHasValue"
          id="metadata-checkbox-2"
          class="checkbox-input"
          v-model="formData.readOnlyIfHasValue"
        />
        <label for="metadata-checkbox-2">
          Read Only If Has Value
          <tooltip :text="TOOLTIP_TEXT.READ_ONLY_IF_HAS_VALUE"></tooltip>
        </label>
      </div>
      <div class="input-block grid-row-4-5">
        <label>
          Input Type
          <span class="required">*</span>
          <tooltip :text="TOOLTIP_TEXT.INPUT_TYPE"></tooltip>
        </label>
        <select name="inputType" required @change="selectOnChange($event)">
          <option value="text" :selected="getInputType('text')">Text</option>
          <option value="email" :selected="getInputType('email')">Email</option>
          <option value="number" :selected="getInputType('number')">
            Number
          </option>
        </select>
      </div>
      <div class="input-block grid-row-4-5">
        <label>
          Default Value
          <tooltip :text="TOOLTIP_TEXT.DEFAULT_VALUE"></tooltip>
        </label>
        <input
          type="text"
          v-model="formData.defaultValue"
          name="defaultValue"
        />
      </div>
      <div class="input-block grid-row-4-5">
        <label>
          Prefix
          <tooltip :text="TOOLTIP_TEXT.PREFIX"></tooltip>
        </label>
        <input type="text" v-model="formData.prefix" name="prefix" />
      </div>
      <div class="input-block grid-row-5-6">
        <label>
          Field Name (auto-populated)
          <span class="required">*</span>
          <tooltip :text="TOOLTIP_TEXT.FIELD_NAME"></tooltip>
        </label>
        <input
          type="text"
          v-model="formData.fieldName"
          name="fieldName"
          required
          disabled
        />
      </div>
      <div class="input-group grid-row-5-6">
        <div class="input-block">
          <label>
            Min Length
            <tooltip :text="TOOLTIP_TEXT.MIN_LENGTH"></tooltip>
          </label>
          <input
            type="number"
            v-model="formData.minLength"
            name="minLength"
            class="input-short"
            @keypress="allowNumberInput($event)"
          />
        </div>
        <div class="input-block">
          <label>
            Max Length
            <tooltip :text="TOOLTIP_TEXT.MAX_LENGTH"></tooltip>
          </label>
          <input
            type="number"
            v-model="formData.maxLength"
            name="maxLength"
            class="input-short"
            @keypress="allowNumberInput($event)"
          />
        </div>
      </div>
      <div class="grid-col-1-4 grid-row-6-7">
        <slot></slot>
      </div>
      <fieldset v-if="!simpleView" class="more-option grid-col-1-4 grid-row-7-8">
        <legend>
          More Options
        </legend>
      </fieldset>
      <dependency-editor
        v-if="!simpleView"
        class="grid-col-1-3 grid-row-8-9"
        :dependency="getDependency()"
        :fieldName="this.formData.fieldName"
        @addDependency="addDependency($event)"
        @removeDependency="removeDependency($event)"
      ></dependency-editor>
    </div>
  </div>
</template>

<script>
import { ROOT_NODE, TOOLTIP_TEXT } from "../mixins/constant-mixin";
import { populateFieldName } from "../mixins/component-utils-mixin";
import TypeField from "./TypeField.vue";
import Tooltip from "./Tooltip.vue";
import DependencyEditor from "./DependencyEditor.vue";
import { Bus } from "../main";

export default {
  name: "InputTextModal",
  props: {
    metadata: {
      type: Object,
      required: true,
    },
    state: {
      type: Object,
    },
    simpleView: {
      type: Boolean,
    },
    limitCompType: {
      type: String,
    },
  },
  data() {
    return {
      formData: structuredClone(this.metadata),
      rootNode: ROOT_NODE.FIELDS,
      TOOLTIP_TEXT: TOOLTIP_TEXT,
      populateFieldName: populateFieldName,
    };
  },
  watch: {
    metadata: function () {
      this.formData = structuredClone(this.metadata);
    },
    state: function () {
      if (this.state.closing) {
        if (this.state.saving) {
          this.$emit("metadataSaved", this.formData);
        } else {
          this.formData = structuredClone(this.metadata);
        }
      }
    },
    simpleView: function() {
      this.insertIntoComponentModal();
    },
  },
  components: {
    "type-field": TypeField,
    "tooltip": Tooltip,
    "dependency-editor": DependencyEditor,
  },
  mounted() {
    this.insertIntoComponentModal();

    Bus.$on("sendDupFieldName", (list) => this.checkDupFieldName(list));
  },
  beforeDestroy() {
    Bus.$off("sendDupFieldName");
  },
  methods: {
    getInputType: function (type) {
      let inputType = "text";
      if (this.metadata.inputType) {
        inputType = this.metadata.inputType;
      }
      return type.toLowerCase() === inputType.toLowerCase();
    },
    getDependency: function () {
      if (this.formData.dependency) {
        return this.formData.dependency;
      }
      return { parent: "", displayIfValues: [] };
    },
    addDependency: function (obj) {
      if (!this.formData.dependency) {
        this.formData.dependency = { parent: "", displayIfValues: [] };
      }
      this.formData.dependency.parent = obj.parent;
      this.formData.dependency.displayIfValues = obj.displayIfValues;
    },
    selectOnChange: function (event) {
      this.formData[event.target.name] = event.target.value;
    },
    removeDependency: function () {
      this.$delete(this.formData, "dependency");
    },
    getCompType: function (type) {
      return type.toLowerCase() === this.formData.type.toLowerCase();
    },
    insertIntoComponentModal: function () {
      const gridRow = "3 / 4";
      Bus.$emit("insertIntoComponentModal", this.$refs.body, gridRow);
    },
    // 1) Populate field name
    // 2) Emit event to FormEditor to check duplicate
    // 3) FormEditor send the list of duplicate field name using Bus
    // 4) If the list is not empty, adjust the field name
    generateFieldName: function () {
      const fieldName = populateFieldName(this.formData, 'label');
      if (fieldName) {
        Bus.$emit("getDupFieldName", fieldName);
      }
    },
    // If there is duplicate field name, append field name with counter
    checkDupFieldName: function (list) {
      const fieldName = this.formData.fieldName;
      if (list.length > 0) {
        let count = 1;
        let temp = fieldName ;
        while (list.find(name => name == temp)) {
          temp = fieldName + (count++);
        }
        count--;
        this.formData.fieldName = temp;
      }
    },
    allowNumberInput: function (event) {
      if (!/[0-9]/.test(event.key)) {
        event.preventDefault();
      }
    },
  },
};
</script>

<style scoped>
.metadata-modal {
  width: 100%;
}

.body {
  padding: 1rem 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-row-gap: 1rem;
}

.body > div {
  padding: 0 2rem;
}

.input-block {
  display: flex;
  flex-direction: column;
}

.input-block input,
.input-block select {
  padding: 8px;
  margin-top: 4px;
}

.checkbox-block {
  align-self: center;
  margin-left: 0.5rem;
  display: flex;
  align-items: center;
}

.checkbox-input {
  margin-right: 8px;
  width: 16px;
  height: 16px;
}

.input-group {
  display: flex;
}

.input-group > .input-block:not(:last-child) {
  margin-right: 3rem;
}

.input-short {
  text-align: center;
  width: 6rem;
}

/* Hide arrow in number input */
/* Chrome and safari */
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.grid-col-1-3 {
  grid-column: 1 / 3;
}

.grid-col-1-4 {
  grid-column: 1 / 4;
}

.grid-row-2-3 {
  grid-row: 2 / 3;
}

.grid-row-3-4 {
  grid-row: 3 / 4;
}

.grid-row-4-5 {
  grid-row: 4 / 5;
}

.grid-row-4-6 {
  grid-row: 4 / 6;
}

.grid-row-5-6 {
  grid-row: 5 / 6;
}

.grid-row-6-7 {
  grid-row: 6 / 7;
}

.grid-row-7-8 {
  grid-row: 7 / 8;
}

.grid-row-8-9 {
  grid-row: 8 / 9;
}

.more-option {
  margin: 0;
  padding: 0;
  border: none;
  border-top: 1px solid #808080;
  text-align: center;
}

.more-option > legend {
  padding: 0 0.5rem;
}

.required {
  color: #f00;
}
</style>